/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../../core'

import {KTSVG, SourceType, detailPageUrl, DetailPage} from '../../../../helpers'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import {
  KTCard,
  KTCardBody,
  KTCardHeader,
  Notes,
  Timeline,
  PaymentStatus,
  DeleteModal,
  ArchiveModal,
  CheckoutDetail,
  Select,
  PaymentPlan,
  Category,
  Official,
  InputPrice,
  Loading,
  Datepicker,
} from '../../../../components'

import {useDispatch, useSelector} from 'react-redux'
import {
  expenseinvoiceTaxGetId,
  expenseinvoiceDelete,
  expenseinvoiceArchiving,
} from '../../../../redux/slices/expenseinvoice.slice'
import {checkoutExpenseInvoicePayMoney} from '../../../../redux/slices/checkout.slice'
import {bankCashAccountGetAll, basicLookup} from '../../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../../utils/performanceOptimizations'

import Dropdown from 'react-bootstrap/Dropdown'

const selectExpenseInvoiceData = (state) => ({
  detail: state.expenseinvoice.detail,
  isDeleted: state.expenseinvoice.isDeleted,
  isArchived: state.expenseinvoice.isArchived,
  error: state.expenseinvoice.error,
  loading: state.expenseinvoice.loading
})

const selectLookupData = (state) => ({
  accountLookupList: state.lookup.accountLookupList,
  YesNo: state.lookup.YesNo
})

const selectCheckoutData = (state) => ({
  transaction: state.checkout.transaction
})

const selectPaymentPlanData = (state) => ({
  paymentPlan: state.paymentplan.paymentPlan,
  isDeleted: state.paymentplan.isDeleted
})

const selectOfficialData = (state) => ({
  officialExpense: state.official.officialExpense,
  isDeleted: state.official.isDeleted
})

function Detail() {
  const [show, setShow] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [isCollapse, setIsCollapse] = useState(true)
  const [checkoutId, setCheckouId] = useState()

  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const pageUrl = useMemoizedPageUrl(location)

  const {detail, isDeleted, isArchived, error, loading} = useSelector(selectExpenseInvoiceData)
  const {accountLookupList, YesNo} = useSelector(selectLookupData)
  const {transaction} = useSelector(selectCheckoutData)
  const {paymentPlan, isDeleted: paymentDelete} = useSelector(selectPaymentPlanData)
  const {officialExpense, isDeleted: officialExpenseDelete} = useSelector(selectOfficialData)

  const dispatch = useDispatch()

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      PaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      FromAccountId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      Payment: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      IsOfficial: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    ExpenseInvoiceId: Number(id),
    PaymentDate: new Date(),
    FromAccountId: '',
    Payment: 0,
    Name: '',
    IsOfficial: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(checkoutExpenseInvoicePayMoney(values))

    formik.setFieldValue('Payment', 0)
    formik.setFieldValue('Name', '')
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handleDelete = useCallback(() => {
    dispatch(expenseinvoiceDelete({id}))
  }, [dispatch, id])

  const handleArchive = useCallback(() => {
    dispatch(expenseinvoiceArchiving({id}))
  }, [dispatch, id])

  useEffect(() => {
    dispatch(expenseinvoiceTaxGetId({id}))
    dispatch(bankCashAccountGetAll())
    dispatch(basicLookup('YesNo'))
  }, [dispatch, id, transaction, paymentPlan, paymentDelete, officialExpense, officialExpenseDelete])

  useEffect(() => {
    if (isDeleted || isArchived) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, isArchived, navigate, pageUrl])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.EXPENSES'}),
      path: '/expenses/expenses',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.EXPENSES.TAX.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail.name}</div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <Link
                                to={detailPageUrl(DetailPage.Project, detail?.projectId)}
                                className='text-gray-400 text-hover-primary'
                                target='_blank'
                              >
                                <KTSVG
                                  path='/images/icons/nav/Projects.svg'
                                  className='svg-icon svg-icon-2 fs-4 me-1'
                                />
                                {detail?.projectName}
                              </Link>
                            </div>

                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <KTSVG
                                path='/images/icons/duotune/general/gen014.svg'
                                className='svg-icon svg-icon-2 fs-4 me-1'
                              />
                              {intl.formatDate(new Date(detail?.editDate), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Category
                            SourceId={Number(id)}
                            SourceTypeId={SourceType.ExpenseInvoice}
                          />
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item
                                onClick={() => setShowArchive(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.ARCHIVE'})}
                                </div>
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => setShow(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'EXPENSEINVOICE.GETFAST.TOTALPRICE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {intl.formatNumber(detail?.totalPrice, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.PAID'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {intl.formatNumber(detail?.totalPrice - detail?.balance, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'EXPENSEINVOICE.GETFAST.BALANCE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {intl.formatNumber(detail?.balance, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'EXPENSEINVOICE.GETFAST.DEFERMENTDATE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {intl.formatDate(new Date(detail?.defermentDate), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail?.invoiceKindName}</div>
                          </div>

                          {detail?.totalOfficalExpense > 0 && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({
                                  id: 'SALEINVOICE.CREATE.TOTALOFFICIALEXPENSE',
                                })}
                              </div>
                              <div className='fw-bold fs-4'>
                                {intl.formatNumber(detail?.totalOfficalExpense, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>

              <KTCard>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    {detail?.checkoutModels?.length > 0 && (
                      <Tab
                        eventKey='checkout'
                        title={intl.formatMessage({id: 'SALEINVOICE.CREATE.CHECKOUTS'})}
                      >
                        <div className='table-responsive'>
                          <table className='table align-middle gy-5 fs-6 table-striped table-hover'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.FROMACCOUNTNAME',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.DESCRIPTION',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.PAYMENTDATE',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.CHECKOUTPAYMENTYPENAME',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.ISOFFICIALNAME',
                                  })}
                                </th>
                                <th>{intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}</th>
                                <th className='text-end'>
                                  {intl.formatMessage({
                                    id: 'EXPENSEINVOICE.GETDETAIL.CHECKOUTMODELS.PAYMENT',
                                  })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail?.checkoutModels.map((item, index) => (
                                <tr key={index} role='button' onClick={() => setCheckouId(item.id)}>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {item.fromAccountName}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {item?.name}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {intl.formatDate(new Date(item?.paymentDate), {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {item.checkoutProcessTypeName}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {item.isOfficialName}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6'>
                                      {item.lastModifiedBy} <br />{' '}
                                      {intl.formatDate(new Date(item?.lastModifiedDate), {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                      })}
                                    </div>
                                  </td>
                                  <td className='p-5'>
                                    <div className='text-dark fw-semibold d-block mb-1 fs-6 text-end'>
                                      {intl.formatNumber(item.amount, {
                                        style: 'currency',
                                        currency: 'try',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 4,
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>

                            <tfoot>
                              <tr className='align-middle fw-bold text-gray-700'>
                                <th colSpan={5}>&nbsp;</th>
                                <th className='fs-4 p-5'>
                                  {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.BALANCE'})}
                                </th>
                                <th className='text-end fs-4 text-nowrap p-5'>
                                  {intl.formatNumber(detail?.balance, {
                                    style: 'currency',
                                    currency: 'try',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  })}
                                </th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </Tab>
                    )}

                    <Tab
                      eventKey='plans'
                      title={intl.formatMessage({id: 'SALEINVOICE.CREATE.PLANS'})}
                    >
                      <PaymentPlan
                        ExpenseInvoiceId={id}
                        data={detail?.paymentPlans}
                        ProjectId={detail?.projectId}
                      />
                    </Tab>

                    <Tab
                      eventKey='official'
                      title={intl.formatMessage({id: 'SALEINVOICE.CREATE.OFFICIAL'})}
                    >
                      <Official ExpenseInvoiceId={id} data={detail?.officialExpenses} />
                    </Tab>
                  </Tabs>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          {detail && (
            <PaymentStatus
              balance={detail?.balance}
              delayDay={detail?.delayDay}
              payment={detail?.totalPrice}
            />
          )}

          {detail?.balance > 0 && (
            <KTCard className='mb-5'>
              <KTCardHeader
                title={intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.ADD'})}
                collapsed={isCollapse}
                onCollapse={(event) => setIsCollapse(event)}
              />

              <KTCardBody className='pt-5' collapseble isShow={isCollapse}>
                <form
                  className='form position-relative mb-6'
                  onSubmit={formik.handleSubmit}
                  noValidate
                >
                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.PAYMENTDATE'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('PaymentDate')}
                        onChange={([date]) => formik.setFieldValue('PaymentDate', date)}
                      />
                      {formik.touched.PaymentDate && formik.errors.PaymentDate && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.PaymentDate}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.TOACCOUNDID'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <Select
                        data={accountLookupList}
                        required
                        {...formik.getFieldProps('FromAccountId')}
                        onChange={(item) =>
                          formik.setFieldValue('FromAccountId', Number(item?.value))
                        }
                      />
                      {formik.touched.FromAccountId && formik.errors.FromAccountId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.FromAccountId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.ISOFFICIAL'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <Select
                        data={YesNo}
                        required
                        {...formik.getFieldProps('IsOfficial')}
                        onChange={(item) =>
                          formik.setFieldValue('IsOfficial', item ? item.value === 1 : '')
                        }
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.PAYMENT'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <InputPrice
                        {...formik.getFieldProps('Payment')}
                        onChange={(value) => formik.setFieldValue('Payment', Number(value))}
                      />
                      {formik.touched.Payment && formik.errors.Payment && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Payment}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'EXPENSEINVOICE.GETDETAIL.RECIEVE.NAME'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        rows={2}
                        {...formik.getFieldProps('Name')}
                      />
                    </div>
                  </div>

                  <div className='text-center'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </KTCardBody>
            </KTCard>
          )}

          <Notes SourceId={Number(id)} SourceTypeId={SourceType.ExpenseInvoice} />
          <Timeline
            isRefresh={loading}
            SourceId={Number(id)}
            SourceTypeId={SourceType.ExpenseInvoice}
            collapse={false}
          />
        </div>
      </div>

      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={() => setShow(false)}
        error={error}
      />

      <ArchiveModal
        onClick={handleArchive}
        onOpen={showArchive}
        onClose={() => setShowArchive(false)}
        error={error}
      />

      <CheckoutDetail id={checkoutId} onClose={() => setCheckouId(null)} />

      {loading && <Loading />}
    </>
  )
}
export {Detail}
